import React from 'react';
import { Link } from 'react-router-dom';

import { AppSidebar } from '@/components/app-sidebar';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

/**
 * Throwaway preview of the shadcn `sidebar-07` block at /ui-preview.
 *
 * Deliberately mounted OUTSIDE the app's own <Layout>: sidebar-07 is a whole
 * app shell, so rendering it inside the existing shell would nest two sidebars
 * and tell you nothing about how it really looks.
 *
 * Now wired to the REAL navigation (Layout/navigation.js) and the real session,
 * so permission gating applies — sign in first or the nav renders empty.
 * Delete this file and its route once you've decided whether to adopt it.
 */
const SidebarPreview = () => (
  <SidebarProvider>
    <AppSidebar />
    <SidebarInset>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink href="#">shadcn sidebar-07</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>Preview</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="rounded-xl border bg-card p-6 text-card-foreground">
          <h2 className="text-lg font-semibold">sidebar-07 preview</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Collapsible icon sidebar. Try the trigger in the header, the team
            switcher at the top, the collapsible nav groups, and the user menu
            at the bottom. Resize the window to see the mobile sheet.
          </p>
          <p className="mt-4 text-sm">
            Showing the dashboard&apos;s real navigation, filtered by your role&apos;s
            permissions. Items come from the same source as the existing sidebar.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-block text-sm text-primary-600 hover:underline"
          >
            ← Back to the real dashboard
          </Link>
        </div>

        {/* Enough height to show the sidebar holding its own while scrolling. */}
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-video rounded-xl bg-muted/50" />
          ))}
        </div>
      </div>
    </SidebarInset>
  </SidebarProvider>
);

export default SidebarPreview;
